import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  discardAnalyticsQueue,
  enrichAttributionWithGeo,
  initAnalytics,
  saveAttributionData,
} from "@/features/analytics";
import backIcon from "./assets/icon-back.svg";
import cookieIcon from "./assets/icon-cookie.svg";
import settingsIcon from "./assets/icon-settings.svg";
import {
  getCookieConsent,
  setCookieConsent,
  type CookieConsentChoice,
} from "./consent";

const COOKIE_POLICY_HREF = "/cookies.html";

const CHOICE_DETAILS = [
  {
    title: "Accept all",
    description:
      "Allow all cookies, including those used for analytics and personalized advertising.",
  },
  {
    title: "Necessary only",
    description:
      "Allow strictly necessary cookies, which include analytics, to ensure the site works properly and to understand how it is used.",
  },
  {
    title: "Decline all",
    description: "Decline non-essential cookies and continue browsing.",
  },
] as const;

const cardClassName =
  "fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-[61] flex max-h-[calc(100dvh-32px)] w-[min(calc(100%-2rem),468px)] -translate-x-1/2 flex-col gap-4 overflow-auto rounded-[16px] bg-white p-4 shadow-[0_0_20px_rgba(26,22,19,0.2)]";

const titleClassName =
  "min-w-0 flex-1 font-display text-xl font-bold leading-6 text-mf-neutral-900";

const bodyClassName =
  "font-body text-sm font-normal leading-4 text-mf-neutral-600";

const linkClassName =
  "text-mf-green underline decoration-solid [text-underline-position:from-font]";

const focusClassName =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mf-green";

const primaryButtonClassName = `flex h-[42px] items-center justify-center rounded-[8px] bg-mf-neutral-900 px-6 font-body text-sm font-bold leading-4 text-white ${focusClassName}`;

const declineButtonClassName = `flex h-[42px] w-full items-center justify-center rounded-[8px] bg-[rgba(26,22,19,0.1)] px-6 font-body text-sm font-bold leading-4 text-mf-neutral-900 ${focusClassName}`;

function CookiePolicyLink({ children }: { children: string }) {
  return (
    <a
      href={COOKIE_POLICY_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClassName}
    >
      {children}
    </a>
  );
}

function CookieConsentCard({
  onChoose,
}: {
  onChoose: (choice: CookieConsentChoice) => void;
}) {
  const [screen, setScreen] = useState<"summary" | "choices">("summary");

  if (screen === "choices") {
    return (
      <div
        className={cardClassName}
        role="dialog"
        aria-modal="true"
        aria-label="Your cookie choices"
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Back"
                onClick={() => setScreen("summary")}
                className={`flex size-7 shrink-0 items-center justify-center ${focusClassName}`}
              >
                <img src={backIcon} alt="" />
              </button>
              <h2 className={titleClassName}>Your cookie choices</h2>
            </div>
            <p className={bodyClassName}>
              You can choose whether to accept or decline the cookies described
              below before continuing.
            </p>
          </div>

          <div className="flex flex-col gap-2 rounded-[16px] bg-[rgba(26,22,19,0.05)] p-3">
            {CHOICE_DETAILS.map((choice, index) => (
              <div key={choice.title} className="contents">
                {index > 0 && (
                  <div className="h-px w-full bg-[rgba(26,22,19,0.1)]" />
                )}
                <div className="flex flex-col">
                  <p className="font-body text-sm font-medium leading-4 text-mf-neutral-900">
                    {choice.title}
                  </p>
                  <p className={bodyClassName}>{choice.description}</p>
                </div>
              </div>
            ))}
          </div>

          <p className={bodyClassName}>
            <CookiePolicyLink>Read more about cookies</CookiePolicyLink>
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => onChoose("all")}
            className={`${primaryButtonClassName} w-full`}
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => onChoose("necessary")}
            className={`${primaryButtonClassName} w-full`}
          >
            Necessary only
          </button>
          <button
            type="button"
            onClick={() => onChoose("declined")}
            className={declineButtonClassName}
          >
            Decline all
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cardClassName}
      role="dialog"
      aria-modal="true"
      aria-label="This website uses cookies"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <img src={cookieIcon} alt="" className="shrink-0" />
          <h2 className={titleClassName}>This website uses cookies</h2>
        </div>
        <p className={bodyClassName}>
          We use our own and third-party cookies to analyse our services and
          show ads related to your preferences, according to profiling based on
          your browsing habits (for example, websites visited).{" "}
          <CookiePolicyLink>Read more</CookiePolicyLink>
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChoose("all")}
          className={`${primaryButtonClassName} min-w-0 flex-1`}
        >
          Accept all
        </button>
        <button
          type="button"
          aria-label="Cookie settings"
          onClick={() => setScreen("choices")}
          className={`flex size-[42px] shrink-0 items-center justify-center rounded-[8px] border border-[rgba(26,22,19,0.2)] bg-white ${focusClassName}`}
        >
          <img src={settingsIcon} alt="" />
        </button>
      </div>
    </div>
  );
}

export function CookieConsent() {
  const [visible, setVisible] = useState(() => getCookieConsent() === null);
  const choosing = useRef(false);

  const choose = (choice: CookieConsentChoice) => {
    if (choosing.current) return;
    choosing.current = true;
    setCookieConsent(choice);
    setVisible(false);

    if (choice === "declined") {
      discardAnalyticsQueue();
      return;
    }

    saveAttributionData();
    void enrichAttributionWithGeo().finally(() => {
      initAnalytics();
    });
  };

  useEffect(() => {
    if (!visible) return;

    const root = document.getElementById("root");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    root?.setAttribute("inert", "");

    return () => {
      document.body.style.overflow = previousOverflow;
      root?.removeAttribute("inert");
    };
  }, [visible]);

  if (!visible) return null;

  return createPortal(
    <>
      <div
        aria-hidden
        className="fixed inset-0 z-[60] bg-transparent touch-none"
      />
      <CookieConsentCard onChoose={choose} />
    </>,
    document.body,
  );
}
